import type { Game, GameContext, GameMeta, InputState } from '../types';
import { AstroEngine } from './AstroEngine';
import { playShoot, playExplosion, playPowerup } from '../../audio/sfx';

export class AstroGame implements Game {
  readonly meta: GameMeta = {
    id: 'astro',
    title: 'NEON TACTICS: GRAVITY SURGE',
    blurb: 'Tactical vector shooter. Trap invaders in stasis, pulse kinetic gravity fields & deflect plasma!',
    tag: 'TACTICAL',
    status: 'playable',
  };

  private ctx!: GameContext;
  private canvas!: HTMLCanvasElement;
  private canvasCtx!: CanvasRenderingContext2D;
  private engine!: AstroEngine;
  private screenShake = 0;

  async mount(ctx: GameContext): Promise<void> {
    this.ctx = ctx;
    this.canvas = document.createElement('canvas');
    this.canvas.className = 'game-canvas';
    this.canvas.setAttribute('aria-label', 'Neon Tactics game canvas');

    ctx.mount.innerHTML = '';
    ctx.mount.appendChild(this.canvas);

    this.canvasCtx = this.canvas.getContext('2d')!;

    const rect = ctx.mount.getBoundingClientRect();
    const w = rect.width || 800;
    const h = rect.height || 600;

    this.engine = new AstroEngine({ width: w, height: h, initialLives: 3 });
    this.resize(w, h);
  }

  resize(w: number, h: number): void {
    if (!this.canvas) return;
    const dpr = window.devicePixelRatio || 1;
    this.canvas.width = w * dpr;
    this.canvas.height = h * dpr;
    this.canvas.style.width = `${w}px`;
    this.canvas.style.height = `${h}px`;

    if (this.canvasCtx) {
      this.canvasCtx.scale(dpr, dpr);
    }
  }

  update(dt: number, input: InputState): void {
    if (!this.engine || !this.canvasCtx) return;

    const bands = this.ctx.getBands();
    const energy = bands.energy;
    const kick = bands.kick;

    const engineInput = {
      left: input.left,
      right: input.right,
      up: input.up,
      down: input.down,
      fire: input.fire,
      firePressed: input.firePressed,
      pulse: input.fire, // secondary pulse or fire
      pulsePressed: input.firePressed,
      restart: input.firePressed,
    };

    const prevBullets = this.engine.bullets.length;
    const prevScore = this.engine.score;
    const prevLives = this.engine.player.lives;

    this.engine.update(dt, engineInput, energy);

    // Audio SFX & Screen Shake triggers
    if (this.engine.bullets.length > prevBullets && input.firePressed) {
      playShoot();
    }
    if (this.engine.score > prevScore + 50) {
      playPowerup();
      this.screenShake = 0.2;
    }
    if (this.engine.player.lives < prevLives) {
      playExplosion();
      this.screenShake = 0.5;
      if (navigator.vibrate) navigator.vibrate([30, 50, 30]);
    }

    if (this.screenShake > 0) {
      this.screenShake -= dt;
    }

    this.render(kick, energy);
  }

  private render(kick: number, _energy: number): void {
    const cCtx = this.canvasCtx;
    const w = this.engine.width;
    const h = this.engine.height;

    cCtx.save();

    // Apply Screen Shake
    if (this.screenShake > 0) {
      const shakeX = (Math.random() - 0.5) * 12 * this.screenShake;
      const shakeY = (Math.random() - 0.5) * 12 * this.screenShake;
      cCtx.translate(shakeX, shakeY);
    }

    // Clear Screen with Retro Dark Grid
    cCtx.fillStyle = '#050711';
    cCtx.fillRect(0, 0, w, h);

    // Pulse Background Grid
    cCtx.strokeStyle = `rgba(0, 240, 255, ${0.08 + kick * 0.12})`;
    cCtx.lineWidth = 1;
    const gridSize = 40;
    for (let x = 0; x < w; x += gridSize) {
      cCtx.beginPath();
      cCtx.moveTo(x, 0);
      cCtx.lineTo(x, h);
      cCtx.stroke();
    }
    for (let y = 0; y < h; y += gridSize) {
      cCtx.beginPath();
      cCtx.moveTo(0, y);
      cCtx.lineTo(w, y);
      cCtx.stroke();
    }

    // Draw Pulses
    for (const p of this.engine.pulses) {
      const alpha = 1 - p.elapsed / p.duration;
      cCtx.strokeStyle = `rgba(0, 255, 200, ${alpha})`;
      cCtx.lineWidth = 4;
      cCtx.beginPath();
      cCtx.arc(p.x, p.y, p.radius, 0, Math.PI * 2);
      cCtx.stroke();
    }

    // Draw Powerups
    for (const p of this.engine.powerups) {
      cCtx.fillStyle = p.type === 'shield' ? '#00e5ff' : p.type === 'overdrive' ? '#ff00ff' : '#ffff00';
      cCtx.beginPath();
      cCtx.arc(p.x, p.y, p.radius, 0, Math.PI * 2);
      cCtx.fill();
    }

    // Draw Bullets
    for (const b of this.engine.bullets) {
      cCtx.fillStyle = b.fromPlayer ? (b.isReflected ? '#00ffaa' : '#ffff00') : '#ff0055';
      cCtx.beginPath();
      cCtx.arc(b.x, b.y, b.radius, 0, Math.PI * 2);
      cCtx.fill();
    }

    // Draw Enemies & Obstacles
    for (const e of this.engine.enemies) {
      cCtx.save();
      cCtx.translate(e.x, e.y);

      if (e.isTrapped) {
        // Bubble Bobble Stasis Bubble
        cCtx.strokeStyle = '#00f0ff';
        cCtx.lineWidth = 2.5;
        cCtx.beginPath();
        cCtx.arc(0, 0, e.radius + 6 + Math.sin(performance.now() * 0.01) * 2, 0, Math.PI * 2);
        cCtx.stroke();
      }

      if (e.type === 'invader') {
        cCtx.strokeStyle = '#ff0055';
        cCtx.lineWidth = 2;
        cCtx.strokeRect(-e.radius, -e.radius, e.radius * 2, e.radius * 2);
      } else if (e.type === 'commander') {
        cCtx.strokeStyle = '#ffbb00';
        cCtx.lineWidth = 2.5;
        cCtx.beginPath();
        cCtx.moveTo(0, -e.radius);
        cCtx.lineTo(e.radius, e.radius);
        cCtx.lineTo(-e.radius, e.radius);
        cCtx.closePath();
        cCtx.stroke();
      } else if (e.type === 'barrier') {
        cCtx.fillStyle = `rgba(0, 200, 255, ${e.hp / e.maxHp})`;
        cCtx.fillRect(-e.radius, -e.radius, e.radius * 2, e.radius * 2);
      } else if (e.type === 'debris') {
        cCtx.strokeStyle = '#aaaaaa';
        cCtx.lineWidth = 1.5;
        cCtx.beginPath();
        cCtx.arc(0, 0, e.radius, 0, Math.PI * 2);
        cCtx.stroke();
      }

      cCtx.restore();
    }

    // Draw Player Ship
    const player = this.engine.player;
    if (player.invulnTimer === 0 || Math.floor(player.invulnTimer * 10) % 2 === 0) {
      cCtx.save();
      cCtx.translate(player.x, player.y);

      // Ship Neon Polygon
      cCtx.strokeStyle = '#00ffcc';
      cCtx.lineWidth = 3;
      cCtx.beginPath();
      cCtx.moveTo(0, -player.radius - 4);
      cCtx.lineTo(player.radius, player.radius);
      cCtx.lineTo(0, player.radius - 6);
      cCtx.lineTo(-player.radius, player.radius);
      cCtx.closePath();
      cCtx.stroke();

      // Shield Aura
      if (player.shield > 0) {
        cCtx.strokeStyle = `rgba(0, 229, 255, ${player.shield / player.maxShield})`;
        cCtx.lineWidth = 2;
        cCtx.beginPath();
        cCtx.arc(0, 0, player.radius + 8, 0, Math.PI * 2);
        cCtx.stroke();
      }

      cCtx.restore();
    }

    cCtx.restore();
  }

  getHud() {
    if (!this.engine) return { score: 0, lives: 3, wave: 1 };
    return {
      score: this.engine.score,
      lives: Math.max(0, this.engine.player.lives),
      wave: this.engine.wave,
      message: this.engine.messageTimer > 0 ? this.engine.message : undefined,
    };
  }

  dispose(): void {
    if (this.canvas && this.canvas.parentElement) {
      this.canvas.parentElement.removeChild(this.canvas);
    }
  }
}
